import { AlertCircle, AlertTriangle, CheckCircle, Info, X, ExternalLink } from 'lucide-react';
import { cn, formatTime } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { t } from '@/i18n';

const iconMap = {
  error: AlertCircle,
  warning: AlertTriangle,
  success: CheckCircle,
  info: Info,
};

const colorMap = {
  error: 'text-red-600',
  warning: 'text-yellow-600',
  success: 'text-green-600',
  info: 'text-blue-600',
};

const bgMap = {
  error: 'bg-red-50/80 border-red-200 border-l-4 border-l-red-500',
  warning: 'bg-yellow-50/80 border-yellow-200 border-l-4 border-l-yellow-500',
  success: 'bg-green-50/80 border-green-200 border-l-4 border-l-green-500',
  info: 'bg-blue-50/80 border-blue-200 border-l-4 border-l-blue-500',
};

const sourceRoutes = {
  'supply-chain': '/supply-chain',
  trucks: '/kamiony',
  planning: '/planovani',
  orders: '/objednavky',
};

export const AlertStrip = ({ alerts = [], onDismiss }) => {
  const navigate = useNavigate();
  const [showAll, setShowAll] = useState(false);

  const visibleAlerts = alerts.filter(a => !a.dismissed);
  const displayed = showAll ? visibleAlerts : visibleAlerts.slice(0, 3);

  if (visibleAlerts.length === 0) {
    return (
      <div className="flex items-center gap-3 px-4 py-3 rounded-xl border bg-green-50/80 backdrop-blur-sm border-green-200/60">
        <CheckCircle className="h-4 w-4 text-green-600" />
        <span className="text-sm font-medium text-green-700">{t('alerts.allOperational')}</span>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      {displayed.map((alert) => {
        const Icon = iconMap[alert.type] || Info;
        const route = sourceRoutes[alert.sourceType];

        return (
          <div
            key={alert.id}
            className={cn(
              'flex items-center gap-3 px-4 py-3 rounded-xl border backdrop-blur-sm transition-all duration-200 hover:shadow-md',
              bgMap[alert.type],
            )}
          >
            <Icon className={cn('h-4 w-4 shrink-0', colorMap[alert.type])} />
            <span className="text-sm font-medium flex-1">{t(alert.message)}</span>
            <span className="text-[11px] text-muted-foreground shrink-0" style={{ fontVariantNumeric: 'tabular-nums' }}>{formatTime(alert.timestamp)}</span>

            {route && (
              <Button
                variant="ghost"
                size="sm"
                className="h-6 w-6 p-0 text-muted-foreground hover:text-foreground"
                onClick={() => navigate(route)}
              >
                <ExternalLink className="h-3 w-3" />
              </Button>
            )}

            {onDismiss && alert.type !== 'success' && (
              <Button
                variant="ghost"
                size="sm"
                className="h-6 w-6 p-0 text-muted-foreground hover:text-foreground"
                onClick={() => onDismiss(alert.id)}
              >
                <X className="h-3 w-3" />
              </Button>
            )}

            {alert.type === 'error' && (
              <span className="w-2 h-2 rounded-full bg-red-500 status-pulse-red shrink-0" />
            )}
          </div>
        );
      })}

      {visibleAlerts.length > 3 && (
        <button
          className="text-sm font-medium text-primary/80 hover:text-primary hover:underline px-4"
          onClick={() => setShowAll(!showAll)}
        >
          {showAll ? t('alerts.showLess') : `${t('alerts.showAll')} (${visibleAlerts.length})`}
        </button>
      )}
    </div>
  );
};
